import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { CHART_COLORS } from '../constants';
import { getCurrencySymbol } from '../formatters';
import { type CurrencyKey } from '../types';
import styles from './HorizontalBarChart.module.scss';

interface BarDatum {
  name: string;
  value: number;
  color?: string;
}

interface HorizontalBarChartProps {
  data: BarDatum[];
  currency: CurrencyKey;
  height?: number;
}

const DEFAULT_COLORS = [CHART_COLORS.plum, CHART_COLORS.orangeDark, CHART_COLORS.plumLight];

function CustomTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload: BarDatum; value: number }>;
  currency: CurrencyKey;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const symbol = getCurrencySymbol(currency);
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipLabel}>{item.payload.name}</span>
      <span className={styles.tooltipValue}>
        {symbol}
        {Math.abs(item.value).toLocaleString()}
      </span>
    </div>
  );
}

export default function HorizontalBarChart({
  data,
  currency,
  height = 200,
}: HorizontalBarChartProps) {
  const symbol = getCurrencySymbol(currency);

  return (
    <div className={styles.wrapper}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(v: number) => `${symbol}${(v / 1000).toFixed(0)}k`}
            fontSize={12}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            fontSize={12}
            tick={{ fill: 'var(--color-text-default-soft)' }}
          />
          <Tooltip content={<CustomTooltip currency={currency} />} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={28} activeBar={false}>
            {data.map((entry, index) => (
              <Cell
                key={entry.name}
                fill={entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
